import React, { useState, useEffect, useCallback } from 'react';
import { Lock, ArrowRight, ArrowLeft, Search, Users, BarChart3, Eye, MessageSquare, Clock, TrendingDown, TrendingUp, ChevronDown, ChevronUp, RefreshCw, Download } from 'lucide-react';
import './AdminSection.css';
import AdminProgress from './AdminProgress';

const PATHWAY_NAMES = {
  founders: 'Founders Pathway', employment: 'Employment Pathway', independent: 'Independent Pathway',
  pathway_a: 'Founders Pathway', pathway_b: 'Employment Pathway', pathway_c: 'Independent Pathway',
};

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin-dashboard';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

async function exportFile(url, token, filename) {
  const res = await fetch(url, { headers: authHeaders(token) });
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

/* ======================================================================
   LOGIN — Secure 2FA flow
   ====================================================================== */
function AdminLogin({ onLogin }) {
  const [step, setStep] = useState('check'); // check | setup | login | code | reset | setpw
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupToken, setSetupToken] = useState('');
  const [resetToken, setResetToken] = useState('');

  // On mount, check URL params for setup/reset tokens and auth status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');
    const reset = params.get('reset');
    if (setup) {
      setSetupToken(setup);
      setStep('setpw');
      return;
    }
    if (reset) {
      setResetToken(reset);
      setStep('setpw');
      return;
    }
    // Check if admin account needs setup
    fetch(`${API}/auth-status`).then(r => r.json()).then(d => {
      if (d.needs_setup) setStep('setup');
      else setStep('login');
    }).catch(() => setStep('login'));
  }, []);

  const requestInvitation = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${API}/send-invitation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'hello@springcollective.biz' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setMsg('Invitation sent to hello@springcollective.biz. Check your email.');
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const submitPassword = async () => {
    setLoading(true); setErr('');
    if (pw.length < 12) { setErr('Password must be at least 12 characters.'); setLoading(false); return; }
    try {
      const endpoint = resetToken ? `${API}/reset-password` : `${API}/set-password`;
      const body = resetToken ? { token: resetToken, password: pw } : { token: setupToken, password: pw };
      const res = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setMsg('Password set successfully. You can now log in.');
      // Clean URL
      window.history.replaceState({}, '', '/admin');
      setTimeout(() => { setStep('login'); setMsg(''); setPw(''); }, 2000);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const submitLogin = async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/secure-login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: pw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid credentials');
      onLogin(data.token, data.name);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const submitCode = async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      onLogin(data.token, data.name);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const requestReset = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${API}/request-reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      await res.json();
      setMsg('If that email is registered, a reset link has been sent.');
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div className="adm-login" data-testid="admin-login">
      <div className="adm-login-card">
        <Lock size={28} className="adm-login-icon" />
        <h1>Admin</h1>

        {step === 'check' && <p className="adm-login-sub">Checking account status...</p>}

        {step === 'setup' && (
          <>
            <p className="adm-login-sub">Admin account needs to be set up. Send an invitation to the admin email.</p>
            {msg && <p className="adm-msg" style={{ color: 'var(--forest)' }}>{msg}</p>}
            {err && <p className="adm-err">{err}</p>}
            <button className="adm-btn-primary" onClick={requestInvitation} disabled={loading} data-testid="admin-send-invite-btn">
              {loading ? 'Sending...' : 'Send Invitation Email'}
            </button>
          </>
        )}

        {step === 'setpw' && (
          <>
            <p className="adm-login-sub">{resetToken ? 'Reset your password.' : 'Set your admin password.'} Minimum 12 characters.</p>
            <div className="adm-field">
              <label>New Password</label>
              <input type="password" value={pw} onChange={e => setPw(e.target.value)}
                placeholder="Minimum 12 characters" data-testid="admin-setpw-input"
                onKeyDown={e => e.key === 'Enter' && submitPassword()} />
            </div>
            {msg && <p className="adm-msg" style={{ color: 'var(--forest)' }}>{msg}</p>}
            {err && <p className="adm-err">{err}</p>}
            <button className="adm-btn-primary" onClick={submitPassword} disabled={loading || pw.length < 12} data-testid="admin-setpw-btn">
              {loading ? 'Setting...' : 'Set Password'}
            </button>
          </>
        )}

        {step === 'login' && (
          <>
            <p className="adm-login-sub">Internal use only. Sign in with your admin account.</p>
            <div className="adm-field">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Admin email" data-testid="admin-email-input" />
            </div>
            <div className="adm-field">
              <label>Password</label>
              <input type="password" value={pw} onChange={e => setPw(e.target.value)}
                placeholder="Password" data-testid="admin-password-input"
                onKeyDown={e => e.key === 'Enter' && submitLogin()} />
            </div>
            {err && <p className="adm-err" data-testid="admin-login-error">{err}</p>}
            <button className="adm-btn-primary" onClick={submitLogin} disabled={loading || !pw || !email}
              data-testid="admin-login-btn">
              {loading ? 'Verifying...' : 'Sign In'}
              {!loading && <ArrowRight size={16} />}
            </button>
            <button className="adm-link-btn" onClick={() => setStep('reset')} style={{ marginTop: 12, background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
              Forgot password?
            </button>
          </>
        )}

        {step === 'code' && (
          <>
            <p className="adm-login-sub">Enter the 6-digit code sent to your email.</p>
            {msg && <p className="adm-msg" style={{ color: 'var(--forest)', fontSize: 13, marginBottom: 8 }}>{msg}</p>}
            <div className="adm-field">
              <label>Login Code</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" data-testid="admin-code-input" maxLength={6}
                style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
                onKeyDown={e => e.key === 'Enter' && code.length === 6 && submitCode()} />
            </div>
            {err && <p className="adm-err">{err}</p>}
            <button className="adm-btn-primary" onClick={submitCode} disabled={loading || code.length !== 6}
              data-testid="admin-verify-code-btn">
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button className="adm-link-btn" onClick={() => { setStep('login'); setErr(''); setMsg(''); setCode(''); }}
              style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--stone)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
              Back to login
            </button>
          </>
        )}

        {step === 'reset' && (
          <>
            <p className="adm-login-sub">Enter your admin email to receive a password reset link.</p>
            <div className="adm-field">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Admin email" data-testid="admin-reset-email-input" />
            </div>
            {msg && <p className="adm-msg" style={{ color: 'var(--forest)' }}>{msg}</p>}
            {err && <p className="adm-err">{err}</p>}
            <button className="adm-btn-primary" onClick={requestReset} disabled={loading || !email}
              data-testid="admin-reset-btn">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button className="adm-link-btn" onClick={() => { setStep('login'); setErr(''); setMsg(''); }}
              style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--stone)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ======================================================================
   AGGREGATE DASHBOARD
   ====================================================================== */
function AggregateDashboard({ token }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('all_time');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = period === 'all_time' ? '' : `?period=${period}`;
      const res = await fetch(`${API}/feedback/aggregate${p}`, { headers: authHeaders(token) });
      setData(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="adm-loading">Loading aggregate data...</div>;
  if (!data) return <div className="adm-loading">No data available</div>;

  const maxBar = (obj) => Math.max(...Object.values(obj).map(Number), 1);

  return (
    <div data-testid="admin-aggregate-view">
      {/* Period selector */}
      <div className="adm-period-bar">
        {[['all_time', 'All Time'], ['daily', 'Last 24h'], ['weekly', 'Last 7 Days']].map(([k, label]) => (
          <button key={k} className={`adm-period-btn ${period === k ? 'active' : ''}`}
            onClick={() => setPeriod(k)} data-testid={`period-${k}`}>{label}</button>
        ))}
        <button className="adm-btn-icon" onClick={load} title="Refresh"><RefreshCw size={16} /></button>
        <button className="adm-btn-outline" onClick={() => exportFile(`${API}/export/aggregate-csv`, token, 'spring_feedback.csv')}
          data-testid="export-aggregate-csv" title="End-of-assessment feedback survey, one row per tester"><Download size={14} /> Export CSV</button>
      </div>

      {/* Key metrics */}
      <div className="adm-metrics-row">
        <MetricCard label="Total Attempts" value={data.total_attempts} icon={<Users size={20} />} />
        <MetricCard label="Completed" value={data.completed_attempts} icon={<BarChart3 size={20} />} />
        <MetricCard label="Feedback Responses" value={data.total_feedback} accent icon={<MessageSquare size={20} />} />
        <MetricCard label="Engagement Rate" value={`${data.engagement_rate}%`} sub="Testers who submitted feedback" icon={<TrendingUp size={20} />} />
        <MetricCard label="Attrition Rate" value={`${data.attrition_rate}%`} sub="Started but didn't reach final screen" warn icon={<TrendingDown size={20} />} />
        <MetricCard label="Completion Rate" value={`${data.completion_rate}%`} icon={<Clock size={20} />} />
      </div>

      {/* NPS */}
      {data.q8_nps?.count > 0 && (
        <div className="adm-card">
          <h3>Net Promoter Score</h3>
          <div className="adm-nps-hero">
            <span className={`adm-nps-num ${(data.q8_nps.nps_score || 0) >= 0 ? 'positive' : 'negative'}`}>
              {data.q8_nps.nps_score ?? '-'}
            </span>
            <span className="adm-nps-label">NPS</span>
          </div>
          <p className="adm-sub-text">Average: {data.q8_nps.average} / 10 ({data.q8_nps.count} responses)</p>
          <BarChart data={data.q8_nps.distribution} label="Score" />
        </div>
      )}

      {/* Q1 Relevance */}
      {data.q1_relevance?.count > 0 && (
        <div className="adm-card">
          <h3>Q1: Did the results feel like they were about you?</h3>
          <p className="adm-sub-text">Average: {data.q1_relevance.average} / 5 ({data.q1_relevance.count} responses)</p>
          <BarChart data={data.q1_relevance.distribution} label="Rating" />
        </div>
      )}

      {/* Q2 Why vs What */}
      {Object.keys(data.q2_why).length > 0 && (
        <div className="adm-card">
          <h3>Q2: Did the reasoning show you why, or just tell you what?</h3>
          <BarChart data={data.q2_why} />
        </div>
      )}

      {/* Q3 Pathway match */}
      {Object.keys(data.q3_pathway_match).length > 0 && (
        <div className="adm-card">
          <h3>Q3: Does the suggested pathway feel right?</h3>
          <BarChart data={data.q3_pathway_match} />
        </div>
      )}

      {/* Q5 Feelings */}
      {Object.keys(data.q5_feelings).length > 0 && (
        <div className="adm-card">
          <h3>Q5: How did this make you feel?</h3>
          <BarChart data={data.q5_feelings} />
        </div>
      )}

      {/* Q7 Length */}
      {Object.keys(data.q7_length).length > 0 && (
        <div className="adm-card">
          <h3>Q7: Assessment length</h3>
          <BarChart data={data.q7_length} />
        </div>
      )}

      {/* Q9 Intent */}
      {Object.keys(data.q9_intent).length > 0 && (
        <div className="adm-card">
          <h3>Q9: Would you continue to the paid programme?</h3>
          <BarChart data={data.q9_intent} />
        </div>
      )}

      {/* Q9b Pricing */}
      {data.q9b_pricing && Object.keys(data.q9b_pricing).length > 0 && (
        <div className="adm-card">
          <h3>Q9b: What would you be willing to pay monthly?</h3>
          <BarChart data={data.q9b_pricing} />
        </div>
      )}

      {/* Flags */}
      {Object.keys(data.flags).length > 0 && (
        <div className="adm-card">
          <h3>Flags</h3>
          <div className="adm-flags-grid">
            {Object.entries(data.flags).map(([flag, count]) => (
              <div key={flag} className="adm-flag-item">
                <span className="adm-flag-name">{flag}</span>
                <span className="adm-flag-count">{count}</span>
              </div>
            ))}
          </div>
          {data.call_requests > 0 && (
            <p className="adm-call-note">{data.call_requests} tester{data.call_requests > 1 ? 's' : ''} requested a follow-up call</p>
          )}
        </div>
      )}

      {/* Open responses */}
      {Object.entries(data.open_responses).map(([key, responses]) => (
        responses.length > 0 && (
          <div key={key} className="adm-card">
            <h3>Open Responses: {key.replace(/_/g, ' ').replace(/^q\d+a?\s/, '').replace(/^q/, 'Q')}</h3>
            <div className="adm-open-list">
              {responses.map((r, i) => (
                <div key={i} className="adm-open-item">"{r}"</div>
              ))}
            </div>
          </div>
        )
      ))}

      {/* FB Module Aggregate */}
      {data.fb_module && Object.keys(data.fb_module).length > 0 && (
        <div className="adm-card">
          <h3>Tester Feedback Module (FB1-FB32)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(data.fb_module).sort(([a], [b]) => {
              const na = parseInt(a.replace(/\D/g, '')) || 0;
              const nb = parseInt(b.replace(/\D/g, '')) || 0;
              return na - nb;
            }).map(([qid, info]) => (
              <div key={qid} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '8px 12px', background: 'var(--sand)', borderRadius: 8 }}>
                <span style={{ fontWeight: 700, color: 'var(--forest)', minWidth: 45, fontSize: 13 }}>{qid}</span>
                <span style={{ fontSize: 13, color: 'var(--stone)' }}>{info.count} responses</span>
                {info.avg_rating !== null && info.avg_rating !== undefined && (
                  <span style={{ fontSize: 13, color: 'var(--rose)', fontWeight: 700 }}>Avg: {info.avg_rating}/5</span>
                )}
                {info.choice_counts && Object.keys(info.choice_counts).length > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--stone)' }}>
                    {Object.entries(info.choice_counts).map(([c, n]) => `${c}: ${n}`).join(' | ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   INDIVIDUAL ATTEMPTS LIST + DETAIL
   ====================================================================== */
function AttemptsView({ token }) {
  const [attempts, setAttempts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const loadList = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    try {
      const res = await fetch(`${API}/attempts?${params}`, { headers: authHeaders(token) });
      const d = await res.json();
      setAttempts(d.attempts);
      setTotal(d.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, search, statusFilter]);

  useEffect(() => {
    loadList();
    const t = setInterval(() => loadList(true), 30000);
    return () => clearInterval(t);
  }, [loadList]);

  const loadDetail = async (id) => {
    setSelected(id);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/attempt/${id}`, { headers: authHeaders(token) });
      setDetail(await res.json());
    } catch { setDetail(null); }
    setDetailLoading(false);
  };

  if (selected && detail) {
    return <AttemptDetail data={detail} token={token} onBack={() => { setSelected(null); setDetail(null); }} />;
  }

  const pathwayLabel = (p) => PATHWAY_NAMES[p] || '-';
  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }); }
    catch { return d; }
  };

  return (
    <div data-testid="admin-attempts-view">
      {/* Reset Attempts Tool */}
      <div className="adm-card" style={{ marginBottom: 16, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--rose)' }}>Reset Attempts</span>
          <input type="email" value={resetEmail} onChange={e => { setResetEmail(e.target.value); setResetMsg(''); }}
            placeholder="email@example.com" data-testid="reset-email-input"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', fontSize: 13, borderRadius: 12, border: '1px solid var(--line)', background: 'var(--white)', color: 'var(--ink)', fontFamily: 'inherit', outline: 'none' }} />
          <button className="adm-btn-outline" disabled={resetLoading || !resetEmail.includes('@')}
            data-testid="reset-attempts-btn"
            onClick={async () => {
              setResetLoading(true); setResetMsg('');
              try {
                const res = await fetch(`${API}/reset-attempts`, {
                  method: 'POST',
                  headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: resetEmail }),
                });
                const d = await res.json();
                setResetMsg(d.message || 'Done');
                setResetEmail('');
                loadList();
              } catch { setResetMsg('Failed to reset.'); }
              setResetLoading(false);
            }}>
            {resetLoading ? '...' : 'Reset'}
          </button>
          {resetMsg && <span style={{ fontSize: 12, color: 'var(--forest)' }}>{resetMsg}</span>}
        </div>
      </div>

      <div className="adm-toolbar">
        <div className="adm-search-wrap">
          <Search size={16} />
          <input placeholder="Search name or email..." value={search}
            onChange={e => setSearch(e.target.value)} data-testid="admin-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="admin-status-filter">
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="draft">In progress</option>
        </select>
        <button className="adm-btn-outline" onClick={() => exportFile(`${API}/export/attempts-csv`, token, 'spring_all_attempts.csv')}
          data-testid="export-attempts-csv" title="One row per attempt: status, dates, pathway result and runway"><Download size={14} /> Export All CSV</button>
      </div>

      <p className="adm-count">{total} attempts</p>

      {loading ? <div className="adm-loading">Loading...</div> : (
        <div className="adm-table-wrap">
          <table className="adm-table" data-testid="admin-attempts-table">
            <thead>
              <tr>
                <th>Name / Email</th>
                <th>Status</th>
                <th>Pathway</th>
                <th>Started</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map(a => (
                <tr key={a.attempt_id} className="adm-row" onClick={() => loadDetail(a.attempt_id)}
                  data-testid={`admin-attempt-row-${a.attempt_id}`}>
                  <td>
                    <div className="adm-name-cell">
                      <span className="adm-name">{a.name || '(no name)'}</span>
                      <span className="adm-email">{a.email}</span>
                    </div>
                  </td>
                  <td><span className={`adm-status ${a.status}`}>{a.status}</span></td>
                  <td>{a.suggested_pathway ? pathwayLabel(a.suggested_pathway) : '-'}</td>
                  <td className="adm-date">{formatDate(a.started_at)}</td>
                  <td>{a.has_feedback ? <span className="adm-fb-yes">Yes</span> : <span className="adm-fb-no">-</span>}</td>
                </tr>
              ))}
              {attempts.length === 0 && <tr><td colSpan={5} className="adm-empty">No attempts found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {detailLoading && <div className="adm-loading">Loading attempt detail...</div>}
    </div>
  );
}

/* ======================================================================
   ATTEMPT DETAIL (Section 7.2 cross-reference)
   ====================================================================== */
function AttemptDetail({ data, token, onBack }) {
  const [tab, setTab] = useState('scoring');

  const pathwayLabel = (p) => PATHWAY_NAMES[p] || p || '-';

  return (
    <div data-testid="admin-attempt-detail">
      <button className="adm-btn-back" onClick={onBack} data-testid="admin-back-btn">
        <ArrowLeft size={16} /> Back to Attempts
      </button>
      <div className="adm-detail-header">
        <h2>{data.name || data.email}</h2>
        <div className="adm-detail-meta">
          <span>{data.email}</span>
          <span>Attempt #{data.attempt_number}</span>
          <span className={`adm-status ${data.status}`}>{data.status}</span>
          <button className="adm-btn-outline" onClick={() => exportFile(`${API}/export/attempt/${data.attempt_id}`, token, `spring_attempt_${data.attempt_id.slice(0,8)}.json`)}
            data-testid="export-attempt-json" title="Everything saved for this tester: answers, scoring, report and feedback"><Download size={14} /> Export JSON</button>
        </div>
      </div>

      <div className="adm-tabs" data-testid="admin-detail-tabs">
        {['scoring', 'answers', 'feedback', 'pathway'].map(t => (
          <button key={t} className={`adm-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="adm-tab-content">
        {tab === 'scoring' && <ScoringTab data={data} pathwayLabel={pathwayLabel} />}
        {tab === 'answers' && <AnswersTab data={data} />}
        {tab === 'feedback' && <FeedbackDetailTab data={data} />}
        {tab === 'pathway' && <PathwayTab data={data} pathwayLabel={pathwayLabel} />}
      </div>
    </div>
  );
}

function ScoringTab({ data, pathwayLabel }) {
  const s = data.scoring || {};
  const q18 = data.q18_reading || {};
  const pw = data.pathway_result || {};
  return (
    <div className="adm-tab-inner" data-testid="admin-scoring-tab">
      <div className="adm-card">
        <h3>Q18 Reading (SPRING admin only)</h3>
        <div className="adm-kv-grid">
          <KV label="Reading" value={q18.reading || 'not available'} />
          {q18.intent && <KV label="Intent reading" value={`${q18.intent}: ${q18.intent_reason || ''}`} />}
          {q18.shape && <KV label="Shape reading" value={`${q18.shape}: ${q18.shape_reason || ''}`} />}
          {Object.entries(data.engine_sources || {}).filter(([, v]) => v).map(([k, v]) => <KV key={k} label={k.replace(/_/g, ' ')} value={v} />)}
          {Object.entries(data.timing?.section_times || {}).map(([k, v]) => <KV key={k} label={`${k.replace('_', ' ')} time`} value={`${Math.round(((v && v.duration_seconds) ?? v) / 6) / 10} min`} />)}
        </div>
        {q18.reason && <p style={{ fontSize: 14, color: 'var(--stone)', marginTop: 8, lineHeight: 1.5 }}><strong>Reason:</strong> {q18.reason}</p>}
      </div>
      <div className="adm-card" data-testid="admin-writing-check">
        <h3>Writing Step (SPRING admin only)</h3>
        <p style={{ fontSize: 14, lineHeight: 1.5, margin: '0 0 8px' }}><strong>Reason 2 lead-in check:</strong> {data.writing_admin?.reason_2_leadin_check || 'not available'}</p>
        {(data.writing_admin?.writing_check_notes || []).map((n, i) => (
          <p key={i} style={{ fontSize: 14, color: 'var(--rose)', lineHeight: 1.5, margin: '0 0 6px' }}>{n}</p>
        ))}
      </div>
      <div className="adm-card">
        <h3>Engine Result (Spec 05 v11)</h3>
        <div className="adm-kv-grid">
          <KV label="Result Type" value={s.result_type || s.suggested_pathway || '-'} />
          <KV label="Leading Pathway" value={s.leading_pathway || pathwayLabel(s.suggested_pathway) || '-'} />
          <KV label="Close Pathway" value={s.close_pathway && s.close_pathway !== 'none' ? s.close_pathway : '-'} />
          <KV label="Wording" value={s.wording || '-'} />
          <KV label="Close Call" value={s.is_close_call ? 'Yes' : 'No'} />
          <KV label="Support Flag" value={s.support_flag ? 'On' : 'Off'} />
        </div>
      </div>
      <div className="adm-card">
        <h3>Other Option / Remaining</h3>
        <div className="adm-kv-grid">
          <KV label="Other Option" value={pw.other_option_pathway && pw.other_option_pathway !== 'none' ? pw.other_option_pathway : '-'} />
          <KV label="Remaining" value={pw.remaining_pathway && pw.remaining_pathway !== 'none' ? pw.remaining_pathway : '-'} />
        </div>
      </div>
    </div>
  );
}

function AnswersTab({ data }) {
  const answers = data.answers || {};
  const keys = Object.keys(answers).sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, '')) || 0;
    const nb = parseInt(b.replace(/\D/g, '')) || 0;
    return na - nb;
  });

  const renderVal = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'object' && !Array.isArray(val)) {
      return (
        <div className="adm-answer-obj">
          {Object.entries(val).map(([k, v]) => (
            <div key={k}><span className="adm-akey">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
          ))}
        </div>
      );
    }
    if (Array.isArray(val)) return val.join(', ');
    return String(val);
  };

  return (
    <div className="adm-tab-inner" data-testid="admin-answers-tab">
      {keys.map(qid => (
        <div key={qid} className="adm-answer-row">
          <span className="adm-qid">{qid}</span>
          <div className="adm-answer-val">{renderVal(answers[qid])}</div>
        </div>
      ))}
    </div>
  );
}

function FeedbackDetailTab({ data }) {
  const fb = data.feedback;
  const fbMod = data.fb_module || [];

  if (!fb && fbMod.length === 0) return <div className="adm-tab-inner"><p className="adm-empty-msg">No feedback submitted.</p></div>;

  return (
    <div className="adm-tab-inner" data-testid="admin-feedback-tab">
      {fb && (
        <div className="adm-card">
          <h3>Pilot Feedback</h3>
          <div className="adm-fb-grid">
            {fb.q1_relevance && <FBRow label="Q1 Relevance (1-5)" value={fb.q1_relevance} />}
            {fb.q2_why && <FBRow label="Q2 Why vs What" value={fb.q2_why} />}
            {fb.q3_pathway_match && <FBRow label="Q3 Pathway Match" value={fb.q3_pathway_match} />}
            {fb.q3a_no_pathway_landing && <FBRow label="Q3a No Pathway" value={fb.q3a_no_pathway_landing} />}
            {fb.q4_wrong_open && <FBRow label="Q4 What was wrong" value={fb.q4_wrong_open} quote />}
            {fb.q5_feelings?.length > 0 && <FBRow label="Q5 Feelings" value={fb.q5_feelings.join(', ')} />}
            {fb.q6_learned && <FBRow label="Q6 Learned" value={fb.q6_learned} quote />}
            {fb.q7_length && <FBRow label="Q7 Length" value={fb.q7_length} />}
            {fb.q7a_patience_open && <FBRow label="Q7a Patience" value={fb.q7a_patience_open} quote />}
            {fb.q8_nps !== undefined && fb.q8_nps !== null && <FBRow label="Q8 NPS (0-10)" value={fb.q8_nps} />}
            {fb.q9_intent && <FBRow label="Q9 Intent" value={fb.q9_intent} />}
            {fb.q9a_depends_open && <FBRow label="Q9a Depends" value={fb.q9a_depends_open} quote />}
            {fb.q10_change_open && <FBRow label="Q10 Change" value={fb.q10_change_open} quote />}
            {fb.q11_other_open && <FBRow label="Q11 Other" value={fb.q11_other_open} quote />}
            {fb.phone && <FBRow label="Phone" value={fb.phone} />}
            {fb.best_time && <FBRow label="Best Time" value={fb.best_time} />}
          </div>
        </div>
      )}
      {fbMod.length > 0 && (
        <div className="adm-card">
          <h3>Tester Feedback Module (FB1-FB32)</h3>
          {fbMod.map((item, i) => (
            <div key={i} className="adm-fb-module-row">
              <span className="adm-fb-qid">{item.question_id || `FB${i + 1}`}</span>
              {item.rating_value !== undefined && <span className="adm-fb-rating">Rating: {item.rating_value}</span>}
              {item.text_value && <span className="adm-fb-text">"{item.text_value}"</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PathwayTab({ data, pathwayLabel }) {
  const pr = data.pathway_result || {};
  return (
    <div className="adm-tab-inner" data-testid="admin-pathway-tab">
      <div className="adm-card">
        <h3>Pathway Result</h3>
        <KV label="Chosen Pathway" value={pathwayLabel(pr.chosen_pathway)} />
        {pr.headline && <div className="adm-report-hl">{pr.headline}</div>}
        {pr.reasoning_block && (
          <div className="adm-report-block">
            {pr.reasoning_block.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        {pr.tension_note && <div className="adm-tension"><p>{pr.tension_note}</p></div>}
        {pr.strengths_summary && <div style={{ marginTop: 16 }}><KV label="Strengths" value={pr.strengths_summary} /></div>}
      </div>
    </div>
  );
}

/* ======================================================================
   SHARED COMPONENTS
   ====================================================================== */
function KV({ label, value }) {
  return (
    <div className="adm-kv">
      <span className="adm-kv-label">{label}</span>
      <span className="adm-kv-value">{value}</span>
    </div>
  );
}

function MetricCard({ label, value, sub, icon, accent, warn }) {
  return (
    <div className={`adm-metric ${accent ? 'accent' : ''} ${warn ? 'warn' : ''}`}>
      <div className="adm-metric-icon">{icon}</div>
      <div className="adm-metric-body">
        <span className="adm-metric-num">{value}</span>
        <span className="adm-metric-label">{label}</span>
        {sub && <span className="adm-metric-sub">{sub}</span>}
      </div>
    </div>
  );
}

function BarChart({ data, label = '' }) {
  const entries = Object.entries(data).sort(([a], [b]) => {
    const na = Number(a), nb = Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return 0;
  });
  const max = Math.max(...entries.map(([, v]) => Number(v)), 1);

  return (
    <div className="adm-barchart">
      {entries.map(([k, v]) => (
        <div key={k} className="adm-bar-row">
          <span className="adm-bar-label">{k}</span>
          <div className="adm-bar-track">
            <div className="adm-bar-fill" style={{ width: `${(Number(v) / max) * 100}%`, display: Number(v) ? undefined : 'none' }} />
          </div>
          <span className="adm-bar-val">{v}</span>
        </div>
      ))}
    </div>
  );
}

function FBRow({ label, value, quote }) {
  return (
    <div className="adm-fb-row">
      <span className="adm-fb-label">{label}</span>
      <span className={`adm-fb-value ${quote ? 'quote' : ''}`}>{quote ? `"${value}"` : value}</span>
    </div>
  );
}


/* ======================================================================
   LOGIN LOG
   ====================================================================== */
function LoginLogView({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/login-log`, { headers: authHeaders(token) })
      .then(r => r.json())
      .then(d => setLogs(d.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="adm-loading">Loading login history...</div>;

  return (
    <div className="adm-section" data-testid="admin-login-log">
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Admin Login History</h2>
      <div className="adm-card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px' }}>Time</th>
              <th style={{ padding: '8px 12px' }}>Email</th>
              <th style={{ padding: '8px 12px' }}>Result</th>
              <th style={{ padding: '8px 12px' }}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--line)', background: log.success ? 'var(--white)' : 'var(--blush)' }}>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                  {log.timestamp ? new Date(log.timestamp).toLocaleString('en-NZ') : '-'}
                </td>
                <td style={{ padding: '8px 12px' }}>{log.email}</td>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: log.success ? 'var(--forest)' : 'var(--rose)' }}>
                  {log.success ? 'Success' : 'Failed'}
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--stone)' }}>{log.detail}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: 'var(--stone)' }}>No login history yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ======================================================================
   DATA EXPORT PANEL
   ====================================================================== */
const EXPORT_GUIDE = [
  ['Responses CSV', 'One row per tester. Every assessment answer in question order (Q1 to Q31), then the pathway result and money figures (income, costs, savings, runway).'],
  ['Feedback CSV', 'One row per feedback answer. Shows the tester, the feedback question number, where it was asked (section check-in or end of assessment) and their answer.'],
  ['Drop-off CSV', 'One row per tester. Shows the last section and question they reached, how many questions they answered, and whether they finished.'],
  ['Download all three (one zip file)', 'The Responses, Feedback and Drop-off spreadsheets together in one zip file, for the wave you picked.'],
  ['Export CSV (Aggregate Feedback tab)', 'The end-of-assessment feedback survey only, one row per tester.'],
  ['Export All CSV (All Attempts tab)', 'One row per attempt with status, dates, pathway result and runway. No individual answers.'],
  ['Export JSON (single attempt)', 'Everything saved for one tester: all answers, scoring, the written report and their feedback.'],
];

function ExportGuide() {
  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--line)', paddingTop: 16 }} data-testid="export-guide">
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>What each download contains</h3>
      <dl style={{ margin: 0, display: 'grid', gap: 10 }}>
        {EXPORT_GUIDE.map(([name, desc], i) => (
          <div key={name} data-testid={`export-guide-item-${i}`}>
            <dt style={{ fontSize: 13, fontWeight: 600 }}>{name}</dt>
            <dd style={{ margin: 0, fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{desc}</dd>
          </div>
        ))}
      </dl>
      <p style={{ marginTop: 10, fontSize: 12, color: 'var(--stone)' }}>The Wave filter above applies to the Responses, Feedback, Drop-off and zip downloads only.</p>
    </div>
  );
}

function ExportPanel({ token }) {
  const [wave, setWave] = useState('');
  const [exporting, setExporting] = useState('');

  const doExport = async (type) => {
    setExporting(type);
    const waveParam = wave ? `?wave=${wave}` : '';
    const urlMap = {
      responses: `${API}/export/responses-csv${waveParam}`,
      feedback: `${API}/export/feedback-csv${waveParam}`,
      dropoff: `${API}/export/dropoff-csv${waveParam}`,
      pack: `${API}/export/wave-pack${waveParam}`,
    };
    const nameMap = {
      responses: `spring_responses${wave ? `_wave${wave}` : ''}.csv`,
      feedback: `spring_feedback${wave ? `_wave${wave}` : ''}.csv`,
      dropoff: `spring_dropoff${wave ? `_wave${wave}` : ''}.csv`,
      pack: `spring_wave_pack${wave ? `_wave${wave}` : '_all'}.zip`,
    };
    await exportFile(urlMap[type], token, nameMap[type]);
    setExporting('');
  };

  return (
    <div className="adm-section" data-testid="admin-export-panel">
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Data Export</h2>
      <div className="adm-card" style={{ padding: 20 }}>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 14, fontWeight: 500 }}>Filter by Wave:</label>
          <select value={wave} onChange={e => setWave(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--line)', fontSize: 14 }}
            data-testid="export-wave-filter">
            <option value="">All testers</option>
            <option value="1">Wave 1</option>
            <option value="2">Wave 2</option>
            <option value="3">Wave 3</option>
            <option value="4">Wave 4</option>
            <option value="5">Wave 5</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="adm-btn-primary" onClick={() => doExport('responses')}
            disabled={!!exporting} data-testid="export-responses-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={16} /> {exporting === 'responses' ? 'Exporting...' : 'Responses CSV'}
          </button>
          <button className="adm-btn-primary" onClick={() => doExport('feedback')}
            disabled={!!exporting} data-testid="export-feedback-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={16} /> {exporting === 'feedback' ? 'Exporting...' : 'Feedback CSV'}
          </button>
          <button className="adm-btn-primary" onClick={() => doExport('dropoff')}
            disabled={!!exporting} data-testid="export-dropoff-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={16} /> {exporting === 'dropoff' ? 'Exporting...' : 'Drop-off CSV'}
          </button>
        </div>
        <button className="adm-btn-outline" onClick={() => doExport('pack')}
          disabled={!!exporting} data-testid="export-wave-pack-btn"
          style={{ marginTop: 12, width: '100%', justifyContent: 'center', padding: '12px 16px', fontSize: 14 }}>
          <Download size={16} /> {exporting === 'pack' ? 'Preparing...' : `Download all three${wave ? ` for Wave ${wave}` : ''} (one zip file)`}
        </button>
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--stone)' }}>
          CSVs include financial figures and open in Excel and Google Sheets.
        </p>
        <ExportGuide />
      </div>
    </div>
  );
}

/* ======================================================================
   MAIN ADMIN SECTION
   ====================================================================== */
export default function AdminSection() {
  const [token, setToken] = useState(sessionStorage.getItem('spring_admin_token'));
  const [view, setView] = useState('aggregate');
  const [authChecking, setAuthChecking] = useState(true);

  const handleLogin = (t, name) => {
    sessionStorage.setItem('spring_admin_token', t);
    setToken(t);
    window.dispatchEvent(new Event('admin-auth-change'));
  };

  const handleLogout = () => {
    sessionStorage.removeItem('spring_admin_token');
    setToken(null);
    window.dispatchEvent(new Event('admin-auth-change'));
  };

  useEffect(() => {
    const init = async () => {
      // Check for setup/reset tokens in URL -- skip auto-auth
      const params = new URLSearchParams(window.location.search);
      if (params.get('setup') || params.get('reset')) {
        setToken(null);
        setAuthChecking(false);
        return;
      }

      const existing = sessionStorage.getItem('spring_admin_token');
      if (existing) {
        try {
          const res = await fetch(`${API}/verify`, { headers: authHeaders(existing) });
          if (res.ok) { setToken(existing); setAuthChecking(false); return; }
        } catch { /* fall through */ }
        sessionStorage.removeItem('spring_admin_token');
      }

      setToken(null);
      setAuthChecking(false);
    };
    init();
  }, []);

  if (authChecking) return <div className="adm-loading">Loading...</div>;
  if (!token) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="adm-dashboard" data-testid="admin-dashboard">
      <div className="adm-topbar">
        <div className="adm-topbar-left">
          <h1 className="adm-title">Admin Dashboard</h1>
        </div>
        <div className="adm-topbar-nav">
          <button className={`adm-nav-btn ${view === 'aggregate' ? 'active' : ''}`}
            onClick={() => setView('aggregate')} data-testid="admin-nav-aggregate">
            <BarChart3 size={16} /> Feedback
          </button>
          <button className={`adm-nav-btn ${view === 'attempts' ? 'active' : ''}`}
            onClick={() => setView('attempts')} data-testid="admin-nav-attempts">
            <Users size={16} /> Attempts
          </button>
          <button className={`adm-nav-btn ${view === 'progress' ? 'active' : ''}`}
            onClick={() => setView('progress')} data-testid="admin-nav-progress">
            <TrendingUp size={16} /> Progress
          </button>
          <button className={`adm-nav-btn ${view === 'exports' ? 'active' : ''}`}
            onClick={() => setView('exports')} data-testid="admin-nav-exports">
            <Download size={16} /> Export
          </button>
          <button className={`adm-nav-btn ${view === 'loginlog' ? 'active' : ''}`}
            onClick={() => setView('loginlog')} data-testid="admin-nav-loginlog">
            <Clock size={16} /> Login Log
          </button>
        </div>
        <button className="adm-btn-outline adm-logout" onClick={handleLogout} data-testid="admin-logout-btn">
          Log out
        </button>
      </div>

      <div className="adm-content">
        {view === 'aggregate' && <AggregateDashboard token={token} />}
        {view === 'attempts' && <AttemptsView token={token} />}
        {view === 'progress' && <AdminProgress token={token} />}
        {view === 'exports' && <ExportPanel token={token} />}
        {view === 'loginlog' && <LoginLogView token={token} />}
      </div>
    </div>
  );
}

// Export helper for NavBar to check if admin is logged in
export function isAdminLoggedIn() {
  return !!sessionStorage.getItem('spring_admin_token');
}
